import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import api from '../api/api';

const KEY = 'clientPhoneCallAttempt';
const BASE = '/client-portal/phone/call';
const expired = 'Время на звонок истекло. Начните вход заново.';
const storage = () => process.env.REACT_APP_CLIENT_APP === 'true' ? localStorage : sessionStorage;
function readAttempt() {
  try {
    const value = JSON.parse(storage().getItem(KEY));
    if (/^[A-Za-z0-9_-]{43}$/.test(value?.secret) && /^\+7[0-9]{10}$/.test(value?.callPhone)
      && /^\+7[0-9]{10}$/.test(value?.phone) && value.expiresAt > Date.now()
      && value.expiresAt <= Date.now() + 300000) return value;
  } catch { /* Corrupt storage cannot authorize a user. */ }
  storage().removeItem(KEY);
  return null;
}

export default function PhoneCallLogin({ acceptance, ready, disabled, returnToCart, onComplete, onPending }) {
  const [attempt, setAttempt] = useState(readAttempt);
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [offline, setOffline] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const current = useRef(attempt);
  const generation = useRef(0);
  const callbacks = useRef({ onComplete, onPending });
  callbacks.current = { onComplete, onPending };
  const reset = () => {
    current.current = null; storage().removeItem(KEY); setAttempt(null); setOffline(false);
  };
  const cancelServer = previous => {
    if (previous) api.post(BASE + '/cancel', { attempt_secret: previous.secret }, { skipLoading: true, timeout: 15000 }).catch(() => {});
  };
  const cancel = () => {
    generation.current++; const previous = current.current;
    reset(); setBusy(false); setError(''); cancelServer(previous);
  };
  useEffect(() => {
    callbacks.current.onPending?.(Boolean(attempt) || busy);
  }, [attempt, busy]);
  useEffect(() => () => { generation.current++; callbacks.current.onPending?.(false); }, []);
  const start = async () => {
    const version = ++generation.current;
    setBusy(true); setError('');
    try {
      const { data } = await api.post(BASE + '/start', { phone, ...acceptance, return_to_cart: Boolean(returnToCart) }, { timeout: 20000 });
      const next = { secret: data.attempt_secret, callPhone: data.call_phone, phone: data.phone,
        expiresAt: Date.now() + data.expires_in * 1000 };
      if (version !== generation.current) { cancelServer(next); return; }
      if (!/^[A-Za-z0-9_-]{43}$/.test(next.secret) || !/^\+7[0-9]{10}$/.test(next.callPhone)
          || !/^\+7[0-9]{10}$/.test(next.phone) || !(data.expires_in > 0 && data.expires_in <= 300)) throw new Error('Invalid call check');
      const cart = sessionStorage.getItem('storefrontCart');
      if (cart && cart.length <= 65536) next.cart = cart;
      storage().setItem(KEY, JSON.stringify(next)); current.current = next; setAttempt(next);
    } catch (e) {
      if (version === generation.current) setError(e.response?.data?.detail || 'Не удалось начать проверку. Попробуйте снова.');
    } finally { if (version === generation.current) setBusy(false); }
  };
  useEffect(() => {
    if (!attempt) return undefined;
    let active = true, inFlight = false;
    const isCurrent = () => active && current.current === attempt;
    const poll = async () => {
      if (!isCurrent()) return;
      const left = Math.max(0, Math.ceil((attempt.expiresAt - Date.now()) / 1000));
      setSeconds(left);
      if (!left) { reset(); setError(expired); return; }
      if (inFlight || document.visibilityState === 'hidden' || window.prokatProstAppPaused) return;
      inFlight = true;
      try {
        const { data } = await api.post(BASE + '/status', { attempt_secret: attempt.secret }, { skipLoading: true, timeout: 15000 });
        if (!isCurrent()) return;
        setOffline(false);
        if (data.status === 'completed' && /^pc_[a-f0-9]{64}$/.test(data.token)) {
          if (attempt.cart && !sessionStorage.getItem('storefrontCart')) {
            try { if (Array.isArray(JSON.parse(attempt.cart))) sessionStorage.setItem('storefrontCart', attempt.cart); } catch { /* Ignore corrupt cart. */ }
          }
          reset(); await callbacks.current.onComplete(data.token, data.return_to_cart === true);
        }
      } catch (e) {
        if (!isCurrent()) return;
        if ([400, 401, 403, 409, 422].includes(e.response?.status)) {
          reset(); setError(e.response?.data?.detail || expired);
        } else setOffline(true);
      } finally { inFlight = false; }
    };
    poll();
    const timer = setInterval(poll, 2000);
    document.addEventListener('visibilitychange', poll); window.addEventListener('online', poll); window.addEventListener('focus', poll);
    return () => { active = false; clearInterval(timer); document.removeEventListener('visibilitychange', poll);
      window.removeEventListener('online', poll); window.removeEventListener('focus', poll); };
  }, [attempt]);

  return <Box sx={{ mb: 3 }}>
    <Typography variant="subtitle2" fontWeight={700}>Уже оформлялись на ПВЗ?</Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Войдите по телефону из вашей карточки клиента. Регистрация через звонок не выполняется.</Typography>
    {!attempt ? <>
      <TextField fullWidth label="Телефон клиента ПВЗ" type="tel" autoComplete="tel" value={phone}
        onChange={e => { setPhone(e.target.value); setError(''); }} disabled={busy || disabled} inputProps={{ maxLength: 40 }} sx={{ mb: 1.5 }} />
      <Button fullWidth variant="contained" size="large" startIcon={busy ? <CircularProgress size={18} color="inherit" /> : <PhoneIcon />}
        disabled={busy || disabled || !ready || phone.replace(/[^0-9]/g, '').length < 10} onClick={start}
        sx={{ bgcolor: '#a5652c', '&:hover': { bgcolor: '#895123' }, borderRadius: 2, py: 1.3 }}>Войти по звонку</Button>
      {busy && <Button onClick={cancel}>Отменить проверку</Button>}
    </> : <Alert severity={offline ? 'warning' : 'info'}>
      <Typography>Позвоните сами с номера {attempt.phone} на указанный номер. При двух SIM-картах выберите нужную.</Typography>
      <Button component="a" href={`tel:${attempt.callPhone}`} startIcon={<PhoneIcon />} sx={{ my: 1, fontSize: 20 }}>{attempt.callPhone}</Button>
      <Typography variant="body2">После звонка вернитесь сюда — вход подтвердится автоматически. Осталось {Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, '0')}.</Typography>
      {offline && <Typography variant="body2">Нет связи с сервисом проверки. Повторим автоматически.</Typography>}
      <Button onClick={cancel}>Отменить проверку</Button>
    </Alert>}
    {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
  </Box>;
}
